import Link from 'next/link';
import { NextSeo } from 'next-seo';

import {
  DocsArticleLayout,
  DocsCallout,
  DocsCard,
  DocsCardGrid,
  DocsCodeBlock,
  DocsStep,
  DocsSteps,
  DocsTable,
} from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'where', title: '1. Where the controls are' },
  { id: 'model', title: '2. Choosing a model' },
  { id: 'privacy', title: '3. Model privacy posture' },
  { id: 'prompt', title: '4. The instruction prompt' },
  { id: 'creativity', title: '5. Creativity' },
  { id: 'visitor-choice', title: '6. Letting visitors choose' },
  { id: 'method', title: '7. How to tune without guessing' },
];

const TuneBotPage = () => {
  return (
    <>
      <NextSeo
        title="Tuning Your Bot"
        description="Model selection, system prompt and creativity — what each control does, which ones actually move answer quality, and how to change them without guessing."
        canonical="https://chat.co/docs/tune-bot"
        openGraph={{
          url: 'https://chat.co/docs/tune-bot',
          title: 'Tuning Your Bot | Chat.co',
          description:
            'Model selection, system prompt and creativity — what each control does and which ones actually move answer quality.',
          type: 'article',
          article: {
            publishedTime: '2026-08-30T00:00:00Z',
            modifiedTime: '2026-08-30T00:00:00Z',
            section: 'Getting Started',
            tags: ['model', 'system prompt', 'temperature', 'tuning'],
          },
        }}
        twitter={{ cardType: 'summary_large_image' }}
      />

      <DocsArticleLayout
        category="Getting Started"
        categoryColor="text-green-700"
        title="Tuning your bot"
        description="Three controls decide how your agent behaves. One of them matters far more than the other two."
        readingTime="9 min read"
        lastUpdated="August 2026"
        tableOfContents={tableOfContents}
        prevArticle={{
          title: 'The knowledge upload playbook',
          href: PageLink.docsKnowledgeUpload,
        }}
        nextArticle={{
          title: '7 AI chatbot myths debunked',
          href: PageLink.docsAiMyths,
        }}
        keyTakeaways={[
          'Model, instruction prompt and creativity all live under Tune Bot → Behavior',
          'The instruction prompt moves answer quality more than the model choice does',
          'Models carry a data-retention badge, and you can filter to no-training models only',
          'Creativity is a 0–100 slider; grounded support bots want it low',
          'You can expose a chosen set of models to visitors, or keep the choice to yourself',
        ]}>
        <p className="lead">
          Most teams reach for the model picker first and the prompt last. That is backwards. On a
          grounded knowledge agent, the system prompt and the knowledge base account for the large
          majority of answer quality — the model is real, but it is the third lever, not the first.
        </p>

        <section id="where" className="scroll-mt-28">
          <h2>1. Where the controls are</h2>
          <p>
            Open a bot and click <strong>Tune Bot</strong> in the sidebar, then the{' '}
            <strong>Behavior</strong> view. Three panels, plus a live preview of the bot beside them
            so you can test without leaving the page.
          </p>

          <DocsCardGrid columns={3}>
            <DocsCard icon="tabler:cpu" title="AI Model">
              Which model answers.
            </DocsCard>
            <DocsCard icon="tabler:file-text" title="Instruction Prompt">
              Who the bot is and how it behaves.
            </DocsCard>
            <DocsCard icon="tabler:adjustments" title="Creativity">
              How far it strays from the retrieved text.
            </DocsCard>
          </DocsCardGrid>

          <p>
            The <strong>Appearance</strong> view on the same page covers everything visual — that is
            covered in <Link href={PageLink.docsGettingStarted}>getting started</Link>.
          </p>
        </section>

        <section id="model" className="scroll-mt-28">
          <h2>2. Choosing a model</h2>
          <p>
            Chat.co offers models from OpenAI, Anthropic, Google and xAI directly, plus a wide set
            of models hosted on AWS Bedrock — including open-weight families. The picker groups them
            by provider.
          </p>

          <h3>How to choose, in practice</h3>

          <DocsTable
            headers={['Situation', 'What to reach for']}
            rows={[
              [
                'Ordinary support and FAQ answering',
                'A current mid-tier model. Fast, cheap per message, and on grounded questions the answer is mostly determined by retrieval anyway.',
              ],
              [
                'Long documents, synthesis, nuanced judgement',
                'A frontier-tier model. Worth the cost when the work is genuinely hard rather than merely long.',
              ],
              [
                'Very high volume, latency-sensitive',
                'The smallest tier in a current family. Noticeably faster, and often indistinguishable on simple retrieval questions.',
              ],
              [
                'Strict data-handling requirements',
                'Filter to no-training models — see the next section.',
              ],
            ]}
          />

          <DocsCallout variant="tip" title="Test the change, don't assume it">
            Swapping to a more expensive model feels like an upgrade and frequently changes nothing
            measurable on grounded Q&amp;A. Run the same five questions before and after. If you
            cannot tell the answers apart, keep the cheaper model and put the effort into your
            knowledge base.
          </DocsCallout>
        </section>

        <section id="privacy" className="scroll-mt-28">
          <h2>3. Model privacy posture</h2>
          <p>
            Every model in the picker carries a badge describing what happens to the prompts and
            responses that pass through it. This is a real, checkable property, not marketing
            language.
          </p>

          <DocsTable
            headers={['Posture', 'What it means']}
            rows={[
              [
                <strong key="a">No training</strong>,
                'The provider is contractually excluded from training on your prompts and responses. Every Bedrock-hosted model carries this, because AWS Bedrock’s terms exclude customer content from training across all hosted foundation models.',
              ],
              [
                <strong key="b">No training, shared</strong>,
                'Not trained on, but prompts and responses are shared with the model provider and retained for a limited window for abuse detection and possible human review.',
              ],
              [
                <strong key="c">Unknown</strong>,
                'Chat.co has not made a classification for this route. Direct-API access to OpenAI, Anthropic, Google and xAI sits here — not because training is happening, but because the classification is deliberately withheld until the contractual language has been reviewed.',
              ],
            ]}
          />

          <DocsCallout variant="note" title="“Unknown” means unclassified, not unsafe">
            Chat.co would rather show an honest &ldquo;unknown&rdquo; than guess in either
            direction. Guessing optimistically misleads you; guessing pessimistically slanders a
            provider. If your compliance position needs a definite answer, use the filter below.
          </DocsCallout>

          <p>
            A <strong>no-training only</strong> filter on the model picker hides everything that is
            not in the strictest category — including the &ldquo;shared&rdquo; variant,
            deliberately, since the filter promises the strongest posture available rather than an
            approximation of it.
          </p>

          <p>
            The broader data-handling picture is in{' '}
            <Link href={PageLink.docsSecurityCompliance}>security &amp; compliance</Link>.
          </p>
        </section>

        <section id="prompt" className="scroll-mt-28">
          <h2>4. The instruction prompt</h2>
          <p>
            The highest-leverage field in the product. It defines who the bot is, what it does when
            it does not know, what it refuses, and how it sounds. You can save named prompts and
            switch between them, and there is an <strong>Enhance</strong> action that rewrites a
            rough draft into something more specific.
          </p>

          <h3>What a good prompt covers</h3>

          <DocsSteps>
            <DocsStep title="Identity and scope">
              <p>
                Who the bot represents and what it is for. &ldquo;You are the support assistant for
                Acme&apos;s billing platform&rdquo; is a boundary, not just a label.
              </p>
            </DocsStep>
            <DocsStep title="What to do when the answer isn't there">
              <p>
                The most valuable instruction you can write. Say explicitly: admit it, do not
                improvise, offer a human. Without this the model will fill the gap, because filling
                gaps is what it is good at.
              </p>
            </DocsStep>
            <DocsStep title="Hard refusals">
              <p>
                Anything it must never do — quote prices it cannot verify, give legal or medical
                advice, speculate about a competitor, discuss another customer&apos;s account.
              </p>
            </DocsStep>
            <DocsStep title="Tone and length">
              <p>
                Be concrete. &ldquo;Two or three sentences unless asked for detail&rdquo; is
                actionable; &ldquo;be friendly and professional&rdquo; is not.
              </p>
            </DocsStep>
            <DocsStep title="Escalation triggers">
              <p>
                When to offer <Link href={PageLink.docsHumanHandoff}>handoff</Link>: account
                questions, repeated failure, any sign of frustration, any direct request for a
                person.
              </p>
            </DocsStep>
          </DocsSteps>

          <DocsCodeBlock
            language="text"
            filename="Instruction prompt — shape, not template"
            code={`You are the support assistant for Acme's billing platform. You answer
using only the knowledge base provided to you.

When you cannot answer from the knowledge base, say so plainly and offer
to connect the person with our team. Never guess at pricing, contract
terms, or the state of someone's account — you cannot see account data.

Keep answers to two or three sentences unless asked for more detail.
Cite your sources.

Offer a human whenever: the question is about a specific account, you
have failed to answer twice, the person sounds frustrated, or they ask
for one. Never argue about a request to speak to a person.`}
          />

          <DocsCallout variant="warning" title="A prompt cannot substitute for knowledge">
            No instruction makes a model know something absent from the knowledge base. If answers
            are wrong on facts rather than in tone, the fix is in{' '}
            <Link href={PageLink.docsKnowledgeUpload}>the knowledge base</Link>, and no amount of
            prompt rewriting will get you there.
          </DocsCallout>
        </section>

        <section id="creativity" className="scroll-mt-28">
          <h2>5. Creativity</h2>
          <p>
            A slider from 0 to 100, mapping to the model&apos;s temperature. It controls how far the
            model is willing to move away from the most probable continuation — in practice, how far
            it strays from the text it retrieved.
          </p>

          <DocsTable
            headers={['Range', 'Behaviour', 'Fits']}
            rows={[
              [
                <strong key="a">0–20</strong>,
                'Sticks closely to retrieved text; repeatable answers',
                'Support, documentation, compliance, anything factual',
              ],
              [
                <strong key="b">20–50</strong>,
                'Some rephrasing and synthesis, still well anchored',
                'General assistants, mixed factual and conversational use',
              ],
              [
                <strong key="c">50+</strong>,
                'Varied, more inventive, less predictable',
                'Brainstorming and ideation — rarely what a knowledge agent wants',
              ],
            ]}
          />

          <DocsCallout variant="tip" title="Low is the right default for a knowledge agent">
            The whole promise of a grounded bot is that it says what your documents say. Creativity
            is the setting that erodes that promise. If you are unsure, go low — you will not miss
            the flair.
          </DocsCallout>
        </section>

        <section id="visitor-choice" className="scroll-mt-28">
          <h2>6. Letting visitors choose</h2>
          <p>
            Under <strong>Appearance → Chat Features</strong> you can expose a set of models to
            visitors, so they pick which one answers. Useful for internal tools and technical
            audiences who have their own preferences.
          </p>
          <p>
            For a customer-facing support bot, usually not: it presents a choice most visitors have
            no basis to make, and it makes your answers less predictable across sessions in a way
            that is hard to debug. Pick a model, and own the result.
          </p>
        </section>

        <section id="method" className="scroll-mt-28">
          <h2>7. How to tune without guessing</h2>
          <p>
            The failure mode here is changing three things, seeing an improvement, and never knowing
            which one caused it.
          </p>

          <DocsSteps>
            <DocsStep title="Write down ten questions and their right answers">
              <p>
                Real ones, in the words your customers use. This list is the only thing standing
                between tuning and superstition, and it takes twenty minutes to build.
              </p>
            </DocsStep>
            <DocsStep title="Run them and record what you get">
              <p>
                Use the live preview beside the controls, or Test Bot. Note which answers are wrong
                and, more importantly, <em>how</em> they are wrong.
              </p>
            </DocsStep>
            <DocsStep title="Diagnose before you change anything">
              <p>
                Wrong facts or missing citations mean the knowledge base is the problem. Right facts
                in the wrong shape — too long, too formal, hedging when it should decline — mean the
                prompt is. Inconsistent answers across runs mean creativity is too high. Only when
                none of these fits should you reach for the model.
              </p>
            </DocsStep>
            <DocsStep title="Change one thing, re-run the same ten">
              <p>
                One variable at a time. It is slower for one iteration and much faster over five,
                because you actually learn what worked.
              </p>
            </DocsStep>
          </DocsSteps>

          <DocsCardGrid columns={2}>
            <DocsCard
              icon="tabler:upload"
              title="Knowledge upload playbook"
              href={PageLink.docsKnowledgeUpload}>
              The lever that matters most, in more detail.
            </DocsCard>
            <DocsCard
              icon="tabler:quote"
              title="Citation quality"
              href={PageLink.docsCitationQuality}>
              When answers are right but the sources are not.
            </DocsCard>
          </DocsCardGrid>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default TuneBotPage;
